/**
 * i18n — language context, detection, and translation helpers.
 *
 * Design principles:
 * 1. URL path is the source of truth: `/es/...` = Spanish, anything else = English.
 * 2. No forced redirects — we offer Spanish via a banner on first visit if browser
 *    prefers Spanish, but user must click to opt in.
 * 3. User choice persists via cookie (1-year) so they never see the banner twice.
 * 4. English experience is unchanged when lang = "en" (default).
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import en from "./en.json";
import es from "./es.json";

const DICTIONARIES = { en, es };
const LANG_COOKIE = "lbb_lang";
const COOKIE_MAX_AGE_DAYS = 365;

const LanguageContext = createContext({
  lang: "en",
  setLang: () => {},
  t: (key) => key,
  toLocalizedPath: (path) => path,
});

// ─── Cookie helpers ───
function setCookie(name, value, days) {
  try {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
  } catch (e) {
    // noop — cookies may be blocked
  }
}
function getCookie(name) {
  try {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
  } catch (e) {
    return null;
  }
}

// ─── Detect language from URL path ───
export function langFromPath(pathname) {
  return pathname && pathname.startsWith("/es") && (pathname === "/es" || pathname.startsWith("/es/")) ? "es" : "en";
}

// ─── Build the localized version of a path ───
export function buildLocalizedPath(path, targetLang) {
  if (!path) return targetLang === "es" ? "/es" : "/";
  const hasEsPrefix = path === "/es" || path.startsWith("/es/");
  const stripped = hasEsPrefix ? (path === "/es" ? "/" : path.slice(3)) : path;
  if (targetLang === "es") {
    return stripped === "/" ? "/es" : `/es${stripped.startsWith("/") ? stripped : "/" + stripped}`;
  }
  return stripped.startsWith("/") ? stripped : "/" + stripped;
}

// ─── Deep-key translation lookup ("blog.buyingGuides") ───
function lookupKey(dict, key) {
  const parts = key.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) {
      cur = cur[p];
    } else {
      return undefined;
    }
  }
  return typeof cur === "string" ? cur : undefined;
}

function interpolate(template, vars) {
  if (!vars || typeof template !== "string") return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => (name in vars ? String(vars[name]) : `{${name}}`));
}

// ─── Provider ───
export function LanguageProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(location.pathname);

  // Persist user's choice as cookie whenever they land on /es/ or /
  useEffect(() => {
    setCookie(LANG_COOKIE, lang, COOKIE_MAX_AGE_DAYS);
    // Reflect on <html lang="..."> for accessibility / screen readers
    try {
      document.documentElement.lang = lang === "es" ? "es-US" : "en-US";
    } catch (e) {}
  }, [lang]);

  const setLang = useCallback(
    (targetLang) => {
      const newPath = buildLocalizedPath(location.pathname, targetLang) + (location.search || "");
      navigate(newPath);
    },
    [location.pathname, location.search, navigate]
  );

  const t = useCallback(
    (key, vars) => {
      const dict = DICTIONARIES[lang] || DICTIONARIES.en;
      const fallback = DICTIONARIES.en;
      const value = lookupKey(dict, key) ?? lookupKey(fallback, key) ?? key;
      return interpolate(value, vars);
    },
    [lang]
  );

  const toLocalizedPath = useCallback((path) => buildLocalizedPath(path, lang), [lang]);

  const value = { lang, setLang, t, toLocalizedPath };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

// ─── Public hooks ───
export function useLanguage() {
  return useContext(LanguageContext);
}
export function useTranslation() {
  const { t, lang } = useContext(LanguageContext);
  return { t, lang };
}

// ─── First-visit banner detection ───
export function shouldOfferSpanishBanner(currentLang) {
  if (currentLang === "es") return false;
  // Already chose / dismissed
  const existing = getCookie(LANG_COOKIE);
  const dismissed = getCookie(LANG_COOKIE + "_dismissed");
  if (existing === "en" || dismissed === "1") return false;
  // Browser language hint
  try {
    const langs = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
    return langs.some((l) => typeof l === "string" && l.toLowerCase().startsWith("es"));
  } catch (e) {
    return false;
  }
}

export function dismissSpanishBanner() {
  setCookie(LANG_COOKIE + "_dismissed", "1", COOKIE_MAX_AGE_DAYS);
}

// ─── Axios helper: set header for Spanish traffic ───
// Call this in an axios interceptor so backend can serve ?lang=es responses.
export function currentLangForAxios() {
  if (typeof window === "undefined") return "en";
  return langFromPath(window.location.pathname);
}
